<!-- Film — the sets of one lift that have a clip, and the ones that could.
     A PUSHED DETAIL SCREEN: the bar alone (the lift's name), a back button, no
     large title. See src/pages/SHELL.md.

     WHAT AN ATTEMPT STORES IS A POINTER. `videoAssetId` is
     `ios:<PHAsset.localIdentifier>` or `android:<content URI>` and there is no
     second copy of the file anywhere. A lifter films most sessions for years;
     copying the clips would double the storage of their whole training history,
     and the copy is the one that goes stale and cannot be deleted from Photos.

     THE PRICE OF A POINTER IS THAT IT CAN DANGLE, so this screen has a design
     for every answer the library can give — here, not in a console log:

       ok          the frame, tappable, plays the clip
       missing     a DASHED frame, "This clip's gone from your library.", Relink
       no-access   outside a limited photo selection — and the way to widen it
       unknown     the library could not answer — Check again, never "gone"
       web         no filming at all, said plainly, rather than a dead button

     NO COLOUR CARRIES ANY OF THAT. The only colour in this app is the plates.
     Filmed and gone-missing are told apart by WORDS and by a dashed outline. -->
<template>
  <IonPage class="work-up film-page">
    <AppHeader :title="lift ? lift.name : 'Film'" :back="backTo" back-text="Lifts" />

    <IonContent :fullscreen="true">
      <!-- A record that does not exist is a real state, not a crash: a deep
           link can outlive the thing it points at. -->
      <div v-if="!lift" class="empty">
        <p class="serif empty-title">Not found</p>
        <p class="note">That lift is no longer here.</p>
        <button type="button" class="wu-btn" @click="goHome">Lifts</button>
      </div>

      <div v-else class="wrap">
        <!-- The bar says which lift; this says what the screen is. -->
        <header class="intro">
          <p class="serif lede">Every set, and its clip</p>
          <!-- THE WEB BUILD SAYS SO, ONCE, AND HIDES EVERY FILMING CONTROL.
               A browser cannot hold a durable reference into the camera roll on
               any platform this app ships to, so a Film button here would be a
               button that fails. -->
          <p v-if="!filming" class="note">{{ noFilmReason }}</p>
          <p v-else-if="sets.length" class="note">
            {{ sets.length }} {{ sets.length === 1 ? 'set' : 'sets' }} · {{ filmedCount }} with a clip
          </p>
        </header>

        <!-- LINKING LIVES ON EACH SET'S OWN ROW. A "Link from library" button up
             here used to make a NEW set at the lift's top set to hang the clip
             on — which is how a duplicate 180 × 1 got into somebody's history.
             A clip belongs to a set that was lifted, so the empty state is
             words only: the sets come from the ramp. -->
        <p v-if="!sets.length" class="note empty-line">
          No sets yet. Work up from Lifts and each set lands here, ready to film or link.
        </p>

        <ul v-if="sets.length" class="sets">
          <li v-for="row in sets" :key="row.id" class="set">
            <!-- ---- the clip, in whatever state the library reports ---- -->
            <div class="art">
              <!-- No clip on this set yet: an outline around nothing. -->
              <div v-if="!row.videoAssetId" class="frame frame--none" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="7" width="13" height="10" rx="2" /><path d="M16 11l5-3v8l-5-3" /></svg>
              </div>

              <!-- Filming is not available at all: a clip is attached, and that
                   is all this build can say about it. -->
              <div v-else-if="!filming" class="frame frame--art" aria-hidden="true">
                <svg class="rack" viewBox="0 0 84 112" preserveAspectRatio="none" fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="2"><path d="M18 8v96M66 8v96M8 38h68" /><rect x="10" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><rect x="67" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><path d="M0 96h84" stroke="rgba(255,255,255,0.08)" /></svg>
              </div>

              <!-- Checking. No spinner: a frame that is about to hold a picture. -->
              <div v-else-if="clipOf(row).status === 'checking'" class="frame frame--waiting" aria-hidden="true"></div>

              <!-- There it is. -->
              <button
                v-else-if="clipOf(row).status === 'ok'"
                type="button"
                class="frame frame--art frame--clip"
                :class="{ pic: !!clipOf(row).thumbnail }"
                :disabled="!!busy"
                :aria-label="`Play ${fmt(row.weight)}${unit}, ${repsText(row.reps, row.repsTo)}`"
                @click="watch_(row)"
              >
                <img v-if="clipOf(row).thumbnail" :src="clipOf(row).thumbnail" alt="">
                <svg v-else class="rack" viewBox="0 0 84 112" preserveAspectRatio="none" fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="2" aria-hidden="true"><path d="M18 8v96M66 8v96M8 38h68" /><rect x="10" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><rect x="67" y="30" width="7" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" stroke="none" /><path d="M0 96h84" stroke="rgba(255,255,255,0.08)" /></svg>
                <span class="playdot" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l10.5-6.5z" /></svg></span>
                <span v-if="clipOf(row).durationSeconds" class="dur" aria-hidden="true">{{ dur(clipOf(row).durationSeconds) }}</span>
              </button>

              <!-- Gone, out of reach, or unanswered: a DASHED frame, because the
                   shape has to say "there is nothing behind this" without
                   spending a hue. The words beside it say which. -->
              <div v-else class="frame frame--gone" aria-hidden="true">
                <span>{{ clipOf(row).status === 'missing' ? 'Gone' : clipOf(row).status === 'no-access' ? 'No access' : 'Unknown' }}</span>
              </div>
            </div>

            <div class="body">
              <p class="setline">
                <span class="serif w">{{ fmt(row.weight) }}</span>
                <span class="u">{{ unit }} × {{ repsShort(row.reps, row.repsTo) }}</span>
              </p>
              <time class="when" :datetime="row.performedAt">{{ when(row.performedAt) }}</time>

              <!-- No clip on this set yet. -->
              <template v-if="!row.videoAssetId">
                <p class="state">Not filmed.</p>
                <div v-if="filming" class="acts">
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="film(row)">Film this set</button>
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="link(row)">Link from library</button>
                </div>
              </template>

              <!-- Filming is not available at all: say what is attached, and stop. -->
              <p v-else-if="!filming" class="state">{{ row.videoSource === 'linked' ? 'Linked clip.' : 'Filmed clip.' }}</p>

              <p v-else-if="clipOf(row).status === 'checking'" class="state">Checking.</p>

              <template v-else-if="clipOf(row).status === 'ok'">
                <p class="state">{{ row.videoSource === 'linked' ? 'Linked clip.' : 'Filmed clip.' }}</p>
                <div class="acts">
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="film(row)">Film again</button>
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="link(row)">Relink</button>
                </div>
              </template>

              <!-- Deleted from the library. -->
              <template v-else-if="clipOf(row).status === 'missing'">
                <p class="state">This clip's gone from your library.</p>
                <div class="acts">
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="link(row)">Relink</button>
                </div>
              </template>

              <!-- Outside a limited photo selection. The clip is probably fine;
                   this app just was not given it. So the fix is offered. -->
              <template v-else-if="clipOf(row).status === 'no-access'">
                <p class="state">Photo access doesn't include this clip.</p>
                <div class="acts">
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="settings">Open photo settings</button>
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="link(row)">Relink</button>
                </div>
              </template>

              <!-- The library answered with something unexpected. Saying "gone"
                   here would be a guess, and the guess sends somebody hunting
                   through Photos for a clip that is sitting there. -->
              <template v-else>
                <p class="state">Could not check this clip.</p>
                <div class="acts">
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="recheck(row)">Check again</button>
                  <button type="button" class="wu-btn-quiet act" :disabled="!!busy" @click="link(row)">Relink</button>
                </div>
              </template>
            </div>

            <!-- Quiet, and the same as Timeline's: gone at once, back through
                 the undo bar. A filmed set loses its pointer, never the clip. -->
            <button
              type="button"
              class="drop"
              :disabled="!!busy"
              :aria-label="`Remove the ${fmt(row.weight)} ${unit} set at ${timeOf(row.performedAt)}`"
              @click="dropSet(row)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>
            </button>
          </li>
        </ul>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRoute, useRouter } from 'vue-router';
import { AppHeader } from '../core/ui';
import { toast } from '../core/util.js';
import {
  attempts, saveAttempt, findLift, findKit,
} from '../store.js';
import { isNative } from '../core/native.js';
import { currentKit } from '../lib/seed.js';
import { repsText, rangeTo } from '../lib/loading.js';
import { forgetSets } from '../lib/forget.js';
import {
  canFilm, record, pick, resolve, play, openPhotoSettings, isCancelled,
} from '../lib/liftvideo.js';

const route = useRoute();
const router = useRouter();
// Resolved from the ROUTER, never imported from router.js: that module imports
// product-routes.js, which imports this page, so a top-level `import { HOME }`
// closes a cycle and the binding is still uninitialised when this module runs.
// The whole app booted to a blank screen with no error.
const HOME = router.resolve({ name: 'lifts' }).path;

/** Read ONCE. Whether a build can film does not change while it is running. */
const filming = canFilm();

// TELLING SOMEONE ON A PHONE THAT THIS "NEEDS THE iOS APP" IS A LIE.
// Filming is off for two different reasons and they are not the same sentence.
// On the web there is no way to hold a durable pointer into a photo library, and
// that is permanent. In the native app it means the recorder is not in THIS
// build — which is a build to fetch, not a dead end.
const noFilmReason = isNative
  ? 'This build cannot film yet. The recorder arrives in the next TestFlight build.'
  : 'Filming needs the iOS or Android app. This page still lists what you lifted.';

const lift = computed(() => findLift(route.params.id));
// A lift with no kit of its own lifts on the current one, as every other
// screen reads it — never a weight with no unit.
const unit = computed(() => findKit(lift.value?.kit)?.unit ?? currentKit()?.unit ?? 'kg');

// Back goes to the Ramp for this lift — the screen Film is reached from —
// and to the list when the lift itself has gone.
const backTo = computed(() => (lift.value ? `/tabs/lifts/${lift.value.id}/ramp` : HOME));
const goHome = () => router.replace(HOME);

/** This lift's sets, newest first. */
const sets = computed(() => attempts.value
  .filter((a) => String(a.lift) === String(lift.value?.id))
  .slice()
  .sort((a, b) => String(b.performedAt).localeCompare(String(a.performedAt))));

const filmedCount = computed(() => sets.value.filter((a) => a.videoAssetId).length);

/**
 * What the library last said about each set's clip, keyed by attempt id.
 *
 * The asset id is kept alongside the answer so a relink re-asks rather than
 * showing yesterday's thumbnail for today's clip.
 */
const clips = reactive({});
const clipOf = (row) => clips[row.id] ?? { status: 'checking' };

/** One action at a time. The camera and the picker are both modal. */
const busy = ref('');

async function check(row) {
  const assetId = row.videoAssetId;
  clips[row.id] = { assetId, status: 'checking' };
  try {
    const answer = await resolve(assetId);
    clips[row.id] = { assetId, ...answer };
  } catch {
    // NOT 'missing'. A failed question is not a deleted clip, and telling
    // somebody their set is gone when it is not is the one wrong answer here.
    clips[row.id] = { assetId, status: 'unknown' };
  }
}

const recheck = (row) => check(row);

// Ask about a clip when it appears, and again whenever its pointer changes.
watch(sets, (rows) => {
  if (!filming) return;
  for (const row of rows) {
    const want = row.videoAssetId || '';
    if (!want) { delete clips[row.id]; continue; }
    if (clips[row.id]?.assetId === want) continue;
    check(row);
  }
}, { immediate: true, deep: false });

/** Save a new pointer onto an existing set. */
function attach(row, assetId, source) {
  saveAttempt({ ...row, videoAssetId: assetId, videoSource: source });
}

function report(e) {
  if (isCancelled(e)) return;          // backing out is not a failure
  toast(e?.code === 'no-access' ? 'No access to the library.' : 'Could not do that.');
}

async function run(key, work) {
  if (busy.value) return;
  busy.value = key;
  try { await work(); } catch (e) { report(e); } finally { busy.value = ''; }
}

const film = (row) => run(`film:${row.id}`, async () => {
  const shot = await record();
  attach(row, shot.assetId, 'recorded');
});

const link = (row) => run(`link:${row.id}`, async () => {
  const picked = await pick();
  if (!picked) return;
  attach(row, picked.assetId, 'linked');
});

// `watch_` because `watch` is Vue's. The verb is the right one for the button.
const watch_ = (row) => run(`play:${row.id}`, () => play(row.videoAssetId));

const settings = () => run('settings', () => openPhotoSettings());

/** A weight, without a trailing `.0` on the whole numbers most sets are. */
const fmt = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
};

/** "1", "3–5": the count after the ×. */
const repsShort = (reps, to) => {
  const n = Number(reps) || 0;
  const t = rangeTo(n, to);
  return t ? `${n}–${t}` : String(n);
};

/** A clip's length the way a player shows it: 0:38. */
const dur = (seconds) => {
  const s = Math.round(Number(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const stamp = new Intl.DateTimeFormat(undefined, {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});
const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : stamp.format(d);
};

/** "12:32" — the time alone, for naming a set in a label (as Timeline does). */
const clockFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const timeOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : clockFmt.format(d);
};

/** Remove a set, reversibly — lib/forget.js, shared with Timeline. */
function dropSet(row) {
  const what = `${fmt(row.weight)} ${unit.value} set removed`;
  forgetSets([row], row.videoAssetId ? `${what} · the clip stays in Photos.` : `${what}.`);
}
</script>

<style scoped>
/* The drawn clip frame, from screens.py `thumb()`: its own two greys per scheme,
   because a video frame is dark in both. */
.film-page { --thumb: #2A2D30; --thumb2: #3A3E42; }
@media (prefers-color-scheme: dark) {
  .film-page { --thumb: #23272B; --thumb2: #31363B; }
}

.wrap { padding: 8px var(--wu-pad) 24px; }

.empty { padding: 48px var(--wu-pad) 0; text-align: center; }
.empty-title { margin: 0 0 8px; font-size: 28px; line-height: 1.1; }
.empty .wu-btn { margin-top: 20px; min-width: 10rem; }

/* ---- the words at the top ---- */
.intro { display: flex; flex-direction: column; gap: 6px; }
.lede { margin: 0; font-size: 18px; font-style: italic; line-height: 1.2; color: var(--dim); }
.note { margin: 0; max-width: 40ch; font: 400 0.75rem/1.5 var(--face-mono); color: var(--dim); }
.empty-line { margin-top: 26px; padding-top: 16px; border-top: 1px solid var(--line); }

/* ---- the list ---- */
.sets { list-style: none; margin: 26px 0 0; padding: 0; }

.set {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr) 44px;
  column-gap: 16px;
  padding: 16px 0;
  border-top: 1px solid var(--line);
}

/* The remove: a drawn ✕ on the weight's line, its glyph on the page's right
   edge, dim until pressed. No hue — the undo bar is what makes it safe. */
.drop {
  align-self: start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -10px -16px 0 16px; /* the glyph's edge on the page's edge */
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  color: var(--dim);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.drop:active { background: var(--seg); color: var(--ink); }
.drop:disabled { color: var(--faint); cursor: default; }

.body { display: flex; flex-direction: column; min-width: 0; }

.setline { display: flex; align-items: baseline; gap: 6px; margin: 2px 0 6px; }
.w { font-size: 28px; line-height: 1; font-variant-numeric: tabular-nums; }
.u { font: 400 0.8125rem/1 var(--face-mono); color: var(--dim); white-space: nowrap; }

.when { font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); }

/* The word that carries the state. There is no coloured dot anywhere here. */
.state { margin: 8px 0 0; font: 400 0.75rem/1.5 var(--face-mono); color: var(--ink); }

.acts { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.act { --wu-btn-h: 44px; padding: 0 14px; font-size: 0.8125rem; }

/* ---- the frame ----
 * One box, four appearances, and the difference between them is SHAPE: a
 * solid frame holds a picture, a dashed one holds nothing. */
.frame {
  position: relative;
  box-sizing: border-box;
  display: block;
  width: 84px;
  height: 112px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
}

.frame--art {
  background: linear-gradient(180deg, var(--thumb2) 0%, var(--thumb) 100%);
  color: #F1F1EE;
}
.frame--clip { cursor: pointer; -webkit-tap-highlight-color: transparent; }
.frame--clip:active { opacity: 0.85; }
.frame--clip[disabled] { opacity: 0.5; }
.frame img, .frame .rack { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }

.frame--waiting { background: var(--seg); }

/* GONE, SAID WITH A DASH. Not red, not a warning triangle — an outline around
   nothing, which is what a dangling pointer actually is. */
.frame--gone,
.frame--none {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed var(--faint);
  background: transparent;
  color: var(--dim);
  font: 400 11px/1.2 var(--face-mono);
}
.frame--none { color: var(--faint); }

.playdot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 34px;
  height: 34px;
  margin: -17px 0 0 -17px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}
.frame--clip.pic .playdot { background: rgba(0, 0, 0, 0.35); }

.dur {
  position: absolute;
  right: 7px;
  bottom: 6px;
  font: 400 10px/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.85);
}
.frame--clip.pic .dur { text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6); }
</style>
